/**
 * Connect section — financial data ingress.
 * One card per connector that this app cares about. Status badge, single-tap
 * connect, optional file upload, status-aware buttons.
 */
import {
  ALL_FINANCIAL_CONNECTORS,
  CONNECTORS_BY_ID,
  HISAAB_CONNECTORS,
  KHAZAANA_CONNECTORS,
  TANKHWAH_CONNECTORS,
} from '../chassis/connectors/financial/index.ts';
import type { Connector, ConnectorContext, ConnectorPayload, ConnectorStatus } from '../chassis/connectors/financial/types.ts';
import { isoTs } from '../chassis/connectors/financial/types.ts';

const APP_ALLOWLIST: Readonly<Record<string, readonly string[]>> = Object.freeze({
  hisaab: HISAAB_CONNECTORS,
  khazaana: KHAZAANA_CONNECTORS,
  tankhwah: TANKHWAH_CONNECTORS,
  all: ALL_FINANCIAL_CONNECTORS.map((c) => c.id),
});

const CONNECTOR_LABELS_HI: Readonly<Record<string, string>> = Object.freeze({
  'sahamati-aa': 'Account Aggregator',
  'gmail-statement': 'Gmail statement',
  'kite-connect': 'Zerodha Kite',
  upstox: 'Upstox',
  groww: 'Groww',
  'angel-one': 'Angel One',
  'mfapi-extended': 'Mutual funds (NAV + CAS)',
  'cams-cas': 'CAMS / Karvy CAS',
  'epfo-passbook': 'EPFO passbook',
  'ais-tis': 'Income Tax AIS',
  'credit-report': 'Credit Bureau',
  'razorpay-history': 'Razorpay merchant',
  'bharat-bill-pay': 'Bharat BillPay',
  'upi-export-parser': 'GPay / PhonePe / Paytm',
  'sms-inbox-android': 'SMS inbox',
});

const UPLOAD_CAPABLE: ReadonlySet<string> = new Set([
  'gmail-statement',
  'cams-cas',
  'epfo-passbook',
  'ais-tis',
  'credit-report',
  'upi-export-parser',
  'mfapi-extended',
]);

interface AuditRow {
  readonly ts: number;
  readonly action: string;
  readonly meta: Record<string, string | number>;
}

class ConnectorRuntime {
  readonly audit: AuditRow[] = [];

  ctx(principalId: string): ConnectorContext {
    const log = (action: string, meta: Record<string, string | number>): void => {
      this.audit.push({ ts: Date.now(), action, meta });
    };
    return Object.freeze({
      principalId,
      nowIso: () => isoTs(),
      dpdpLog: log,
    });
  }
}

const runtime = new ConnectorRuntime();

const TPL = /* html */ `
<style>
:host { display: block; margin: 28px 0 24px; --label-mono: var(--mono, 'Space Mono', monospace); }
.head {
  font-family: var(--label-mono);
  font-size: 10px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--rule-strong, oklch(28% 0.04 50));
  padding-bottom: 8px;
  border-bottom: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.head .sub { font-size: 9px; opacity: 0.7; letter-spacing: 0.28em; }
.grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}
.card {
  border: 1px solid var(--rule-soft, oklch(60% 0.040 50 / 0.45));
  padding: 14px 14px 12px;
  background: oklch(99% 0.005 80);
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  min-height: 138px;
}
.card .name {
  font-family: var(--serif, 'Cormorant Garamond', serif);
  font-size: 18px;
  line-height: 1.15;
  font-weight: 600;
  color: var(--ink-strong, oklch(20% 0.04 50));
}
.card .desc {
  font-family: var(--serif, 'Cormorant Garamond', serif);
  font-size: 13px;
  color: var(--ink-soft, oklch(40% 0.03 50));
  line-height: 1.4;
  margin: 4px 0 8px;
}
.card .meta {
  display: flex;
  gap: 8px;
  font-family: var(--label-mono);
  font-size: 9px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--rule-strong);
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.badge {
  padding: 2px 6px;
  border: 1px solid currentColor;
  border-radius: 2px;
}
.badge.live { color: var(--success, oklch(45% 0.12 145)); }
.badge.mock { color: oklch(45% 0.08 80); }
.badge.on { color: var(--crimson, #9b1c1c); border-color: var(--crimson, #9b1c1c); }
.badge.off { opacity: 0.55; }
.row { display: flex; gap: 8px; margin-top: auto; flex-wrap: wrap; }
button {
  appearance: none;
  font-family: var(--label-mono);
  font-size: 9px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  padding: 6px 11px;
  background: oklch(99% 0.005 80);
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  color: inherit;
  cursor: pointer;
  border-radius: 2px;
}
button[disabled] { opacity: 0.45; cursor: not-allowed; }
button.primary {
  background: var(--ink-strong, oklch(20% 0.04 50));
  color: var(--paper, #f5ead1);
  border-color: transparent;
}
button.danger { color: var(--crimson, #9b1c1c); border-color: currentColor; }
label.file {
  display: inline-flex;
  align-items: center;
  font-family: var(--label-mono);
  font-size: 9px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  padding: 6px 11px;
  background: oklch(99% 0.005 80);
  border: 1px dashed var(--rule-strong, oklch(28% 0.04 50));
  border-radius: 2px;
  cursor: pointer;
}
input[type="file"] { display: none; }
.toast {
  margin-top: 8px;
  font-family: var(--label-mono);
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-soft);
}
.toast.err { color: var(--crimson, #9b1c1c); }
.foot {
  margin-top: 16px;
  font-family: var(--serif, 'Cormorant Garamond', serif);
  font-size: 12px;
  color: var(--ink-soft);
  font-style: italic;
}
</style>
<section>
  <header class="head">
    <span class="title">Connections</span>
    <span class="sub" id="appSlug"></span>
  </header>
  <div class="grid" id="grid"></div>
  <p class="foot">Hr connector test-mode mein hai. Live keys aaye toh ek toggle se prod ho jayega. Sab on-device. DPDP-2023 consent ledger per-connector.</p>
</section>
`;

interface CardState {
  readonly conn: Connector;
  status: ConnectorStatus;
}

export class ConnectSection extends HTMLElement {
  private cards: Map<string, CardState> = new Map();
  private payloads: Map<string, ConnectorPayload> = new Map();

  static get observedAttributes(): readonly string[] {
    return ['app'];
  }

  connectedCallback(): void {
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = TPL;
    const slug = this.getAttribute('app') ?? 'all';
    const slugEl = root.getElementById('appSlug');
    if (slugEl) slugEl.textContent = `Issue II · ${slug}`;
    void this.render(slug);
    this.addEventListener('refresh', () => void this.render(slug));
  }

  attributeChangedCallback(): void {
    if (!this.shadowRoot) return;
    const slug = this.getAttribute('app') ?? 'all';
    void this.render(slug);
  }

  private async render(slug: string): Promise<void> {
    const ids = APP_ALLOWLIST[slug] ?? APP_ALLOWLIST.all ?? [];
    const ctx = runtime.ctx(`${slug}@empire`);
    const grid = this.shadowRoot?.getElementById('grid');
    if (!grid) return;
    grid.replaceChildren();
    for (const id of ids) {
      const conn = CONNECTORS_BY_ID[id];
      if (!conn) continue;
      const status = await conn.status(ctx);
      this.cards.set(id, { conn, status });
      grid.appendChild(this.renderCard(slug, conn, status));
    }
  }

  private renderCard(slug: string, conn: Connector, status: ConnectorStatus): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';
    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = CONNECTOR_LABELS_HI[conn.id] ?? conn.displayName;
    const desc = document.createElement('div');
    desc.className = 'desc';
    desc.textContent = conn.displayName;
    const meta = document.createElement('div');
    meta.className = 'meta';
    const modeBadge = document.createElement('span');
    modeBadge.className = `badge ${status.mode}`;
    modeBadge.textContent = status.mode === 'mock' ? 'mock-mode' : 'live';
    const stateBadge = document.createElement('span');
    stateBadge.className = `badge ${status.connected ? 'on' : 'off'}`;
    stateBadge.textContent = status.connected ? 'connected' : 'not connected';
    meta.append(modeBadge, stateBadge);
    if (status.lastSync) {
      const sync = document.createElement('span');
      sync.className = 'badge off';
      sync.textContent = 'synced ' + new Date(status.lastSync as unknown as string).toLocaleDateString('en-IN');
      meta.append(sync);
    }
    const row = document.createElement('div');
    row.className = 'row';
    const toast = document.createElement('div');
    toast.className = 'toast';
    if (!status.connected) {
      const btn = document.createElement('button');
      btn.className = 'primary';
      btn.textContent = 'Connect';
      btn.addEventListener('click', () => void this.handleConnect(slug, conn.id, toast));
      row.append(btn);
    } else {
      const importBtn = document.createElement('button');
      importBtn.textContent = 'Sync';
      importBtn.addEventListener('click', () => void this.handleImport(slug, conn.id, toast));
      row.append(importBtn);
      if (UPLOAD_CAPABLE.has(conn.id)) {
        const label = document.createElement('label');
        label.className = 'file';
        label.textContent = 'Upload';
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/pdf,text/csv,application/json,text/plain';
        input.addEventListener('change', () => {
          const f = input.files?.[0];
          if (f) void this.handleImport(slug, conn.id, toast, f);
        });
        label.append(input);
        row.append(label);
      }
      const dc = document.createElement('button');
      dc.className = 'danger';
      dc.textContent = 'Disconnect';
      dc.addEventListener('click', () => void this.handleDisconnect(slug, conn.id, toast));
      row.append(dc);
    }
    card.append(name, desc, meta, row, toast);
    return card;
  }

  private async handleConnect(slug: string, id: string, toast: HTMLElement): Promise<void> {
    const c = this.cards.get(id);
    if (!c) return;
    const ctx = runtime.ctx(`${slug}@empire`);
    toast.textContent = 'Granting consent and connecting…';
    toast.classList.remove('err');
    const r = await c.conn.connect(ctx);
    if (!r.ok) {
      toast.textContent = `Connect failed: ${r.reason}`;
      toast.classList.add('err');
      return;
    }
    toast.textContent = 'Connected. Tap Sync to import.';
    await this.render(slug);
    this.dispatchEvent(new CustomEvent('connector-connected', { detail: { id }, bubbles: true, composed: true }));
  }

  private async handleImport(slug: string, id: string, toast: HTMLElement, fileBlob?: Blob): Promise<void> {
    const c = this.cards.get(id);
    if (!c) return;
    const ctx = runtime.ctx(`${slug}@empire`);
    toast.textContent = 'Importing…';
    toast.classList.remove('err');
    const r = fileBlob ? await c.conn.import(ctx, { fileBlob }) : await c.conn.import(ctx);
    if (!r.ok) {
      toast.textContent = `Import failed: ${r.reason}`;
      toast.classList.add('err');
      return;
    }
    this.payloads.set(id, r.data);
    const counts = summariseCounts(r.data);
    toast.textContent = r.note ? `${counts} · ${r.note}` : counts;
    await this.render(slug);
    this.dispatchEvent(new CustomEvent('connector-imported', { detail: { id, payload: r.data }, bubbles: true, composed: true }));
  }

  private async handleDisconnect(slug: string, id: string, toast: HTMLElement): Promise<void> {
    const c = this.cards.get(id);
    if (!c) return;
    const ctx = runtime.ctx(`${slug}@empire`);
    const r = await c.conn.disconnect(ctx);
    if (!r.ok) {
      toast.textContent = `Disconnect failed: ${r.reason}`;
      toast.classList.add('err');
      return;
    }
    toast.textContent = 'Disconnected. Stored data unaffected.';
    await this.render(slug);
  }
}

function summariseCounts(p: ConnectorPayload): string {
  const bits: string[] = [];
  if (p.accounts?.length) bits.push(`${p.accounts.length} accts`);
  if (p.transactions?.length) bits.push(`${p.transactions.length} tx`);
  if (p.holdings?.length) bits.push(`${p.holdings.length} holdings`);
  if (p.mfFolios?.length) bits.push(`${p.mfFolios.length} folios`);
  if (p.epf) bits.push('EPF');
  if (p.ais) bits.push('AIS');
  if (p.credit) bits.push(`CIBIL ${p.credit.score}`);
  if (p.bills?.length) bits.push(`${p.bills.length} bills`);
  if (p.razorpay?.length) bits.push(`${p.razorpay.length} payments`);
  if (p.slips?.length) bits.push(`${p.slips.length} slips`);
  return bits.length > 0 ? bits.join(' · ') : 'imported';
}

if (!customElements.get('connect-section')) {
  customElements.define('connect-section', ConnectSection);
}
